import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ProcessPreview = () => {
  const [activeStep, setActiveStep] = useState(0);

  const processSteps = [
    {
      icon: "Search",
      title: "Discover & Learn",
      duration: "2-6 weeks",
      description: "Foundations, concepts, and guided exploration. Master semiconductor fundamentals, physics, and material science through interactive modules.",
      deliverables: ["Fundamental concepts", "Interactive labs", "Material science projects", "Atomic theory mastery"]
    },
    {
      icon: "CircuitBoard",
      title: "Design & Develop",
      duration: "3-8 weeks",
      description: "RTL or Analog design and practical simulations. Build digital circuits, create Verilog designs, and simulate analog circuits with industry tools.",
      deliverables: ["RTL modules", "Analog designs", "Simulation reports", "Design portfolios"]
    },
    {
      icon: "CheckCircle",
      title: "Verify & Implement",
      duration: "2-6 weeks",
      description: "Hands-on testing and layout generation. Learn verification methodology, UVM frameworks, and complete physical design workflows.",
      deliverables: ["UVM testbenches", "Physical layouts", "DRC/LVS reports", "Timing sign-off"]
    },
    {
      icon: "Rocket",
      title: "Launch & Showcase",
      duration: "1-4 weeks",
      description: "Capstone project and portfolio presentation. Work on real-world design problems and showcase your silicon-ready expertise.",
      deliverables: ["Capstone project", "Portfolio presentation", "Industry mentorship", "Job readiness"]
    }
  ];

  return (
    <section className="py-12 sm:py-16 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-primary mb-3 sm:mb-4 color-brand-dark">
            Our Learning Process
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
            Four structured phases that turn learners into professionals. Timeline: 8–24 weeks total depending on course track.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 lg:gap-12 items-start">
          {/* Process Steps */}
          <div className="space-y-4 sm:space-y-6">
            {processSteps?.map((step, index) => (
              <motion.div
                key={index}
                className={`border-2 p-4 sm:p-6 cursor-pointer transition-all duration-300 ${
                  activeStep === index
                    ? 'bg-accent/5' :'border-concrete bg-card hover:border-accent/50'
                }`}
                style={{
                  ...(activeStep === index ? { borderColor: '#000000' } : {}),
                  boxShadow: activeStep === index ? '6px 6px 0px #f4eedd' : '3px 3px 0px #000000'
                }}
                onClick={() => setActiveStep(index)}
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
              >
                <div className="flex items-start space-x-3 sm:space-x-4">
                  <div className={`w-10 h-10 sm:w-12 sm:h-12 border-2 flex items-center justify-center transition-all duration-300 ${
                    activeStep === index ? 'rotate-12' : ''
                  }`}
                  style={activeStep === index ? { backgroundColor: '#000000', borderColor: '#f4eedd' } : { backgroundColor: '#f4eedd', borderColor: '#000000' }}>
                    <Icon
                      name={step?.icon}
                      size={20}
                      className="sm:w-6 sm:h-6"
                      color={activeStep === index ? "#f4eedd" : "#1A1A1A"}
                    />
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 space-y-1 sm:space-y-0">
                      <h3 className="text-base sm:text-lg font-bold text-primary color-brand-dark">{step?.title}</h3>
                      <span className="text-xs font-semibold text-accent bg-accent/10 px-2 py-1 self-start">
                        {step?.duration}
                      </span>
                    </div>
                    <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                      {step?.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Active Step Details */}
          <motion.div
            key={activeStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
            className="bg-card border-2 border-concrete p-4 sm:p-8"
            style={{ boxShadow: '8px 8px 0px #000000' }}
          >
            <div className="flex items-center space-x-3 sm:space-x-4 mb-4 sm:mb-6">
              <div className="w-12 h-12 sm:w-16 sm:h-16 border-2 flex items-center justify-center rotate-12" style={{ backgroundColor: '#000000', borderColor: '#f4eedd' }}>
                <Icon name={processSteps?.[activeStep]?.icon} size={24} className="sm:w-8 sm:h-8 text-white" color="#f4eedd" />
              </div>
              <div>
                <h3 className="text-lg sm:text-2xl font-black text-primary color-brand-dark">
                  {processSteps?.[activeStep]?.title}
                </h3>
                <p className="text-accent font-semibold text-sm sm:text-base">
                  {processSteps?.[activeStep]?.duration}
                </p>
              </div>
            </div>

            <p className="text-muted-foreground mb-4 sm:mb-6 leading-relaxed text-sm sm:text-base">
              {processSteps?.[activeStep]?.description}
            </p>

            <div className="mb-4 sm:mb-6">
              <h4 className="font-bold text-primary mb-3 sm:mb-4 flex items-center text-sm sm:text-base color-brand-dark">
                <Icon name="CheckCircle" size={16} className="sm:w-5 sm:h-5 mr-2 text-accent" />
                Key Deliverables
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                {processSteps?.[activeStep]?.deliverables?.map((deliverable, index) => (
                  <div
                    key={index}
                    className="flex items-center space-x-2 p-2"
                    style={{ backgroundColor: '#f4eedd' }}
                  >
                    <div className="w-2 h-2" style={{ backgroundColor: '#000000' }}></div>
                    <span className="text-xs sm:text-sm font-medium text-primary">{deliverable}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Button
                variant="default"
                className="text-primary font-bold border-2 w-full sm:w-auto"
                style={{ backgroundColor: '#f4eedd', borderColor: '#000000' }}
                iconName="MessageCircle"
                iconPosition="left"
              >
                Join This Phase
              </Button>
              <Button
                variant="outline"
                iconName="ExternalLink"
                iconPosition="right"
                className="w-full sm:w-auto hover:bg-white"
              >
                View Full Curriculum
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ProcessPreview;